import { Link } from "react-router-dom";
import { Gavel, Loader2, DollarSign, Check, Shield, CreditCard, ChevronRight } from "lucide-react";

export const BidPanel = ({
  auction,
  user,
  isUserWinning,
  isUserSeller,
  hasPaymentMethod,
  timeRemaining,
  bidAmount,
  setBidAmount,
  minNextBid,
  increments,
  submitting,
  onBid,
  onBuyNow,
  onQuickBid,
  onSignIn,
}) => (
  <div className="glass-card p-6 mb-4">
    <div className="flex items-center justify-between mb-4">
      <div>
        <p className="text-sm text-[#71717A]">Current Bid</p>
        <p className="text-3xl text-[#D4AF37] font-bold">
          ${(auction.current_bid || auction.starting_bid).toLocaleString()}
        </p>
      </div>
      <div className="text-right">
        <p className="text-sm text-[#71717A]">Bids</p>
        <p className="text-2xl text-[#EDEDED]">{auction.bid_count || 0}</p>
      </div>
    </div>

    {user && (
      <div
        className={`p-3 rounded-lg mb-4 ${
          isUserWinning ? "bg-[#22C55E]/10 border border-[#22C55E]/30" : "bg-[#27272A]"
        }`}
      >
        {isUserWinning ? (
          <p className="text-[#22C55E] flex items-center gap-2">
            <Check className="w-4 h-4" /> You are the high bidder!
          </p>
        ) : isUserSeller ? (
          <p className="text-[#71717A] flex items-center gap-2">
            <Gavel className="w-4 h-4" /> This is your auction
          </p>
        ) : (
          <p className="text-[#A1A1AA]">You are not the high bidder</p>
        )}
      </div>
    )}

    {auction.status === "active" && timeRemaining > 0 && !isUserSeller && (
      <>
        {user && hasPaymentMethod === false && (
          <div
            className="p-4 bg-[#18181B] border border-[#D4AF37]/30 rounded-lg mb-4"
            data-testid="payment-required-notice"
          >
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-5 h-5 text-[#D4AF37]" />
              <p className="text-sm text-[#EDEDED]">Payment method required</p>
            </div>
            <p className="text-xs text-[#71717A] mb-3">
              Add a payment method to bid. Bidding is free — your card is only charged if you win.
            </p>
            <Link
              to={`/dashboard?return_to=${encodeURIComponent(`/auctions/${auction.auction_id}`)}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-medium text-black"
            >
              Add Payment Method <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        )}

        {user && hasPaymentMethod === null && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-[#71717A]">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking bidder verification…
          </div>
        )}

        {user && hasPaymentMethod === true && (
          <>
          <div className="mb-4">
            <label className="block text-sm text-[#A1A1AA] mb-2">Your Bid</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A] text-lg">$</span>
              <input
                type="number"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                className="input-minimal w-full pl-8 text-xl"
                placeholder={minNextBid.toLocaleString()}
                min={minNextBid}
                data-testid="bid-input"
              />
            </div>
            <p className="text-xs text-[#71717A] mt-1">
              Minimum bid: ${minNextBid.toLocaleString()}
            </p>
            {parseFloat(bidAmount) >= 5000 && (
              <p
                className="text-xs text-amber-400 mt-1 flex items-center gap-1"
                data-testid="hold-notice"
              >
                <Shield className="w-3 h-3" /> A 10% hold ($
                {(parseFloat(bidAmount) * 0.1).toLocaleString()}) will be placed on your card.
                Released if outbid.
              </p>
            )}
          </div>
  
          <div className="flex gap-2 mb-4">
            {increments.map((inc) => (
              <button
                key={inc}
                onClick={() => onQuickBid(inc)}
                className="flex-1 py-2 bg-[#27272A] text-[#A1A1AA] rounded-lg text-sm hover:bg-[#3F3F46] transition-colors"
                data-testid={`quick-bid-${inc}`}
              >
                +${inc}
              </button>
            ))}
          </div>
  
          <button
            onClick={onBid}
            disabled={submitting || !bidAmount}
            className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="place-bid-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Gavel className="w-5 h-5" />}
            Place Bid
          </button>
          <p className="mt-2 text-center text-xs text-[#71717A]">
            Winning bidder is charged the final bid plus a 3% processing fee.
          </p>
          </>
        )}
      </>
    )}

    {auction.buy_now_price && auction.status === "active" && timeRemaining > 0 && !isUserSeller && user && hasPaymentMethod === true && (
      <div className="mt-4 pt-4 border-t border-[#27272A]">
        <button
          onClick={onBuyNow}
          disabled={submitting}
          className="w-full py-3 bg-[#22C55E] text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#16A34A] transition-colors disabled:opacity-50"
          data-testid="buy-now-btn"
        >
          <DollarSign className="w-5 h-5" />
          Buy Now - ${auction.buy_now_price.toLocaleString()}
        </button>
        <p className="mt-2 text-center text-xs text-[#71717A]">
          3% processing fee: ${(auction.buy_now_price * 0.03).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {" · "}Total: ${(auction.buy_now_price * 1.03).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>
    )}

    {!user && auction.status === "active" && (
      <button onClick={onSignIn} className="btn-gold w-full mt-4" data-testid="signin-to-bid-btn">
        Sign In to Bid
      </button>
    )}

    {(auction.status !== "active" || timeRemaining <= 0) && (
      <div className="p-4 bg-[#27272A] rounded-lg text-center">
        <p className="text-[#A1A1AA]">
          {auction.status === "payment_protected" ? "Payment Protected" :
            auction.status === "delivery_reported" ? "Buyer Review Period" :
            auction.status === "disputed" ? "Payment Disputed" :
            auction.status === "released" ? "Auction Complete" :
            auction.status === "refunded" ? "Buyer Refunded" :
            auction.status === "no_sale" ? "No Sale" : "Auction Ended"}
        </p>
        {auction.current_bid > 0 && (
          <p className="text-[#D4AF37] text-xl mt-1">
            Final: ${auction.current_bid.toLocaleString()}
          </p>
        )}
      </div>
    )}
  </div>
);
